import { Injectable } from '@angular/core';
import { map, Observable, of } from 'rxjs';
import { HttpBaseService } from './http-base.service';
import {
    PropertyDefinitionCreateDto,
    PropertyDefinitionDetailDto,
    PropertyDefinitionListDto,
    PropertyDefinitionUpdateDto
} from '../dtos/property-definition.types';

@Injectable({
    providedIn: 'root'
})
export class PropertyDefinitionService extends HttpBaseService {
    private readonly baseEndpoint = 'api/PropertyDefinitions';

    /**
     * Get property definitions by object definition id
     */
    getPropertyDefinitionsByObject(objectId: string): Observable<PropertyDefinitionListDto[]> {
        if (!objectId) {
            return of([]);
        }
        return this.get<PropertyDefinitionListDto[]>(`${this.baseEndpoint}/by-object/${objectId}`);
    }

    /**
     * Get property definition by ID
     */
    getPropertyDefinitionById(propertyId: string): Observable<PropertyDefinitionDetailDto> {
        return this.get<PropertyDefinitionDetailDto>(`${this.baseEndpoint}/${propertyId}`);
    }

    /**
     * Get property definition for update
     */
    getPropertyDefinitionForUpdate(propertyId: string): Observable<PropertyDefinitionUpdateDto> {
        return this.get<PropertyDefinitionUpdateDto>(`${this.baseEndpoint}/${propertyId}/update`);
    }

    /**
     * Create a new property definition
     * @returns Observable containing the created property definition's ID
     */
    createPropertyDefinition(propertyDefinition: PropertyDefinitionCreateDto): Observable<{ id: string }> {
        return this.post<{ id: string }>(this.baseEndpoint, propertyDefinition);
    }

    /**
     * Update an existing property definition
     */
    updatePropertyDefinition(propertyDefinition: PropertyDefinitionUpdateDto): Observable<void> {
        return this.put<void>(this.baseEndpoint, propertyDefinition);
    }

    /**
     * Delete a property definition
     */
    deletePropertyDefinition(propertyId: string): Observable<void> {
        return this.delete<void>(`${this.baseEndpoint}/${propertyId}`);
    }

    /**
     * Check if a property code exists for a specific object
     */
    checkCodeExists(code: string, objectId: string): Observable<boolean> {
        return this.get<{ exists: boolean }>(`${this.baseEndpoint}/check-code-exists/${code}/${objectId}`)
            .pipe(map(response => response.exists));
    }

    /**
     * Import property definitions in bulk
     */
    importPropertyDefinitions(definitions: PropertyDefinitionCreateDto[]): Observable<void> {
        return this.post<void>(`${this.baseEndpoint}/import`, definitions);
    }
}